
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const blogPosts = [
    {
      id: 1,
      title: "10 Photography Tips That Increased My Airbnb Bookings by 65%",
      excerpt: "Learn the exact photography techniques that helped one host transform their listing from average to top-performing in just 30 days.",
      category: "photography",
      readTime: "8 min read",
      date: "March 15, 2024",
      image: "https://public.readdy.ai/ai/img_res/245e1f5965e7856eb3d16735ee886725.jpg",
      content: `
        <h2>The Power of Professional Photography</h2>
        <p>When Sarah first listed her downtown condo on Airbnb, she was getting maybe 2-3 bookings per month. After implementing these 10 photography strategies, her bookings increased to 8-10 per month - a 65% increase!</p>
        
        <h3>1. Golden Hour Magic</h3>
        <p>Shoot during the golden hour (1 hour after sunrise or before sunset) for warm, inviting natural light that makes spaces feel cozy and welcoming.</p>
        
        <h3>2. Stage Every Room</h3>
        <p>Remove personal items, add fresh flowers, and arrange furniture to create clear pathways. This simple staging increased Sarah's booking rate by 23%.</p>
        
        <h3>3. Capture the Lifestyle</h3>
        <p>Show guests how they'll feel in your space. Include shots of morning coffee on the balcony, cozy reading nooks, and inviting dining areas.</p>
        
        <h3>4. Wide-Angle Lens Strategy</h3>
        <p>Use a wide-angle lens to make rooms appear larger, but avoid fisheye distortion that can make photos look unnatural.</p>
        
        <h3>5. Vertical Shots for Mobile</h3>
        <p>Include vertical photos for mobile users. 78% of Airbnb searches happen on mobile devices.</p>
        
        <h3>6. HDR Processing</h3>
        <p>Use HDR (High Dynamic Range) processing to balance bright windows with darker interiors, creating evenly lit, professional-looking photos.</p>
        
        <h3>7. Detail Shots Matter</h3>
        <p>Include close-ups of amenities: luxury bedding, modern appliances, unique decor. These details help justify your pricing.</p>
        
        <h3>8. Bathroom Brilliance</h3>
        <p>Clean, well-lit bathroom photos are crucial. Add fresh towels, clear counters, and ensure mirrors are spotless.</p>
        
        <h3>9. Outdoor Spaces</h3>
        <p>If you have a balcony, patio, or garden, showcase it! Outdoor space photos can increase bookings by up to 40%.</p>
        
        <h3>10. Professional Editing</h3>
        <p>Consistent color correction, brightness adjustment, and professional editing make your listing stand out from amateur photos.</p>
        
        <h3>The Results</h3>
        <p>After implementing these strategies and getting professional photo editing, Sarah's listing went from page 3 to page 1 in search results. Her average nightly rate increased by $35, and her occupancy rate jumped from 45% to 78%.</p>
      `
    },
    {
      id: 2,
      title: "How to Price Your Airbnb Photography Investment for Maximum ROI",
      excerpt: "A data-driven analysis of photography costs vs. booking revenue increases. See real numbers from 50+ hosts who invested in professional photos.",
      category: "business",
      readTime: "6 min read",
      date: "March 12, 2024",
      image: "https://readdy.ai/api/search-image?query=Modern%20laptop%20showing%20Airbnb%20analytics%20dashboard%20with%20increasing%20revenue%20graphs%2C%20calculator%20and%20money%20on%20desk%2C%20professional%20business%20setup%20with%20charts%20showing%20growth&width=600&height=400&seq=blog2&orientation=landscape",
      content: `
        <h2>The Photography Investment That Pays for Itself</h2>
        <p>We analyzed data from 50+ Airbnb hosts who invested in professional photography. The results? Every single host saw positive ROI within 60 days.</p>
        
        <h3>Average Investment Breakdown</h3>
        <ul>
          <li>Professional photography session: $300-500</li>
          <li>Photo editing (15-20 photos): $350</li>
          <li>Total investment: $650-850</li>
        </ul>
        
        <h3>Average Revenue Increases</h3>
        <ul>
          <li>Booking rate increase: 45%</li>
          <li>Nightly rate increase: $25-40</li>
          <li>Search ranking improvement: 2-3 pages higher</li>
          <li>Guest satisfaction scores: +0.3 points</li>
        </ul>
        
        <h3>Case Study: Downtown Studio</h3>
        <p>Mark's 500 sq ft studio was earning $1,200/month. After professional photos:</p>
        <ul>
          <li>Monthly revenue increased to $1,950</li>
          <li>ROI achieved in 45 days</li>
          <li>Annual revenue increase: $9,000</li>
        </ul>
        
        <h3>When to Reinvest</h3>
        <p>Update photos every 18-24 months or when you make significant changes to your space. Fresh photos maintain your competitive edge.</p>
      `
    },
    {
      id: 3,
      title: "Before & After: 5 Airbnb Photo Transformations That Doubled Bookings",
      excerpt: "See dramatic before and after photo comparisons from real Airbnb listings, plus the exact editing techniques that made the difference.",
      category: "case-studies",
      readTime: "10 min read",
      date: "March 8, 2024",
      image: "https://readdy.ai/api/search-image?query=Split%20screen%20showing%20before%20and%20after%20comparison%20of%20Airbnb%20room%20photos%2C%20dramatic%20improvement%20in%20lighting%20and%20staging%2C%20professional%20photography%20transformation&width=600&height=400&seq=blog3&orientation=landscape",
      content: `
        <h2>Real Transformations, Real Results</h2>
        <p>These 5 before and after transformations show the power of professional photo editing. Each listing saw booking increases of 80-120%.</p>
        
        <h3>Transformation 1: The Dark Living Room</h3>
        <p><strong>Before:</strong> Dim, uninviting space with poor lighting</p>
        <p><strong>After:</strong> Bright, welcoming room with balanced exposure</p>
        <p><strong>Result:</strong> 95% booking increase, $30 higher nightly rate</p>
        
        <h3>Transformation 2: The Cluttered Kitchen</h3>
        <p><strong>Before:</strong> Personal items everywhere, poor composition</p>
        <p><strong>After:</strong> Clean, staged space highlighting modern appliances</p>
        <p><strong>Result:</strong> 120% booking increase, moved from page 4 to page 1</p>
        
        <h3>Transformation 3: The Bland Bedroom</h3>
        <p><strong>Before:</strong> Unmade bed, harsh lighting, no personality</p>
        <p><strong>After:</strong> Hotel-quality staging with warm, inviting lighting</p>
        <p><strong>Result:</strong> 85% booking increase, guest satisfaction up 0.4 points</p>
        
        <h3>Key Editing Techniques Used</h3>
        <ul>
          <li>HDR processing for balanced lighting</li>
          <li>Color temperature adjustment for warmth</li>
          <li>Perspective correction for professional look</li>
          <li>Selective brightness enhancement</li>
          <li>Virtual staging elements where appropriate</li>
        </ul>
        
        <h3>The Investment vs. Return</h3>
        <p>Average editing cost: $350 for 15-20 photos</p>
        <p>Average monthly revenue increase: $750</p>
        <p>ROI timeline: 2-3 weeks</p>
      `
    },
    {
      id: 4,
      title: "Seasonal Airbnb Photography: Maximizing Bookings Year-Round",
      excerpt: "Learn how to update your listing photos seasonally to maintain high booking rates and attract different types of guests throughout the year.",
      category: "photography",
      readTime: "7 min read",
      date: "March 5, 2024",
      image: "https://readdy.ai/api/search-image?query=Four%20season%20collage%20showing%20same%20Airbnb%20property%20in%20spring%20summer%20fall%20winter%2C%20seasonal%20decorations%20and%20lighting%20changes%2C%20professional%20real%20estate%20photography&width=600&height=400&seq=blog4&orientation=landscape",
      content: `
        <h2>Adapting Your Photos for Every Season</h2>
        <p>Smart hosts update their photos seasonally to maintain competitive advantage. Here's how to maximize bookings year-round.</p>
        
        <h3>Spring Photography Strategy</h3>
        <ul>
          <li>Highlight fresh flowers and greenery</li>
          <li>Open windows to show fresh air flow</li>
          <li>Capture morning light streaming in</li>
          <li>Show outdoor spaces coming to life</li>
        </ul>
        
        <h3>Summer Optimization</h3>
        <ul>
          <li>Emphasize cooling features (AC, fans)</li>
          <li>Showcase outdoor entertaining areas</li>
          <li>Highlight proximity to summer activities</li>
          <li>Use bright, energetic lighting</li>
        </ul>
        
        <h3>Fall Appeal</h3>
        <ul>
          <li>Add warm, cozy elements</li>
          <li>Show heating features</li>
          <li>Include autumn decor touches</li>
          <li>Emphasize indoor comfort</li>
        </ul>
        
        <h3>Winter Warmth</h3>
        <ul>
          <li>Highlight heating systems</li>
          <li>Show cozy reading nooks</li>
          <li>Include warm lighting scenes</li>
          <li>Emphasize indoor entertainment</li>
        </ul>
        
        <h3>ROI of Seasonal Updates</h3>
        <p>Hosts who update photos seasonally see 25% higher year-round occupancy rates compared to those with static photos.</p>
      `
    },
    {
      id: 5,
      title: "The Psychology of Airbnb Photos: What Makes Guests Click 'Book Now'",
      excerpt: "Discover the psychological triggers in photography that influence booking decisions, backed by behavioral research and booking data analysis.",
      category: "business",
      readTime: "9 min read",
      date: "March 1, 2024",
      image: "https://readdy.ai/api/search-image?query=Psychology%20concept%20with%20brain%20imagery%20overlaid%20on%20beautiful%20Airbnb%20interior%20photos%2C%20warm%20inviting%20lighting%2C%20emotional%20connection%20to%20space%2C%20modern%20behavioral%20design&width=600&height=400&seq=blog5&orientation=landscape",
      content: `
        <h2>The Science Behind Booking Decisions</h2>
        <p>Understanding guest psychology can increase your booking rate by up to 60%. Here's what behavioral research tells us about effective Airbnb photography.</p>
        
        <h3>Emotional Triggers That Drive Bookings</h3>
        <ul>
          <li><strong>Safety & Security:</strong> Well-lit entrances, secure locks visible</li>
          <li><strong>Comfort & Relaxation:</strong> Soft textures, warm lighting</li>
          <li><strong>Social Connection:</strong> Dining areas, conversation spaces</li>
          <li><strong>Achievement:</strong> Workspace areas, productivity zones</li>
        </ul>
        
        <h3>Color Psychology in Action</h3>
        <ul>
          <li><strong>Blue:</strong> Trust and reliability (+15% booking rate)</li>
          <li><strong>Green:</strong> Relaxation and nature (+12% longer stays)</li>
          <li><strong>Warm whites:</strong> Cleanliness and comfort (+20% satisfaction)</li>
          <li><strong>Earth tones:</strong> Stability and grounding (+8% repeat bookings)</li>
        </ul>
        
        <h3>The 3-Second Rule</h3>
        <p>Guests decide whether to explore your listing within 3 seconds of seeing your main photo. Key elements that capture attention:</p>
        <ul>
          <li>Clear focal point (usually the main living area)</li>
          <li>Balanced composition with rule of thirds</li>
          <li>Warm, inviting lighting</li>
          <li>Uncluttered, clean spaces</li>
        </ul>
        
        <h3>Data-Driven Results</h3>
        <p>Listings with psychologically optimized photos show:</p>
        <ul>
          <li>43% higher click-through rates</li>
          <li>31% more booking inquiries</li>
          <li>25% higher guest satisfaction scores</li>
          <li>18% increase in repeat bookings</li>
        </ul>
      `
    }
  ];

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'photography', label: 'Photography Tips' },
    { id: 'business', label: 'Business Strategy' },
    { id: 'case-studies', label: 'Case Studies' }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Airbnb Photography Blog</h1>
            <p className="text-xl text-blue-100 mb-8">
              Expert tips, case studies, and strategies to maximize your Airbnb bookings through professional photography
            </p>
            <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-yellow-300">500+</div>
                  <div className="text-blue-100">Hosts Helped</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-yellow-300">45%</div>
                  <div className="text-blue-100">Avg. Booking Increase</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-yellow-300">$850</div>
                  <div className="text-blue-100">Avg. Monthly Revenue Boost</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>

            {/* Blog Posts Grid */}
            <div className="grid lg:grid-cols-2 gap-8">
              {filteredPosts.map(post => (
                <article key={post.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                        {categories.find(cat => cat.id === post.category)?.label}
                      </span>
                      <span>{post.date}</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 hover:text-blue-600 transition-colors cursor-pointer">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <button className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                      Read Full Article
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {/* Newsletter Signup */}
            <div className="mt-20 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="max-w-2xl mx-auto text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Get Weekly Airbnb Photography Tips
                </h3>
                <p className="text-gray-600 mb-6">
                  Join 2,500+ hosts receiving our weekly newsletter with actionable photography tips, 
                  case studies, and exclusive strategies to boost your bookings.
                </p>
                <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Subscribe Free
                  </button>
                </form>
                <p className="text-sm text-gray-500 mt-3">
                  No spam. Unsubscribe anytime. 95% of subscribers say our tips increased their bookings.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
